/**
 * Google OAuth Authentication Service
 * 
 * This service handles Google OAuth authentication flow:
 * 1. User clicks "Continue with Google"
 * 2. Google OAuth popup opens
 * 3. User authorizes the app
 * 4. Google returns an ID token
 * 5. We send the token to our backend
 * 6. Backend verifies and creates/logs in the user
 */

// API_URL already includes /api, so we don't add it again
const API_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

/**
 * Authenticate user with Google OAuth token
 * @param {string} token - Google ID token from OAuth response
 * @param {string} role - User role ('candidate' or 'hr')
 * @returns {Promise<Object>} User data and session info
 */
export const googleAuth = async (token, role = 'candidate') => {
  try {
    console.log('🔐 [GOOGLE AUTH] Sending request:', {
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 30)}...` : 'NO TOKEN',
      role,
      url: `${API_URL}/auth/google`
    });

    const response = await fetch(`${API_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        role,
      }),
    });

    const data = await response.json();

    console.log('📥 [GOOGLE AUTH] Response:', {
      status: response.status,
      ok: response.ok,
      hasData: !!data
    });

    if (!response.ok) {
      console.error('❌ [GOOGLE AUTH] Error response:', data);
      throw new Error(data.detail || 'Google authentication failed');
    }

    return data;
  } catch (error) {
    console.error('❌ Google auth error:', error);
    throw error;
  }
};

/**
 * Handle Google OAuth callback with authorization code
 * @param {string} code - Authorization code from Google
 * @param {string} role - User role ('candidate' or 'hr')
 * @returns {Promise<Object>} User data and session info
 */
export const googleAuthCallback = async (code, role = 'candidate') => {
  try {
    const response = await fetch(`${API_URL}/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        role,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Google authentication failed');
    }

    return data;
  } catch (error) {
    console.error('❌ Google callback error:', error);
    throw error;
  }
};

/**
 * Initialize Google OAuth
 * This should be called when the app loads
 */
export const initGoogleAuth = () => {
  // Load Google OAuth script
  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
};
